// src/features/monetize/usePreviewGrant.ts

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MONETIZE_QUERY_KEYS } from '@/lib/constants/monetize';
import { MonetizeApi } from './MonetizeApi';
import { useMonetizeStatus } from './useMonetizeStatus';

/**
 * A preview pass for the panel's own player previews (Revenue Suite Seq 1 step 8):
 * when Monetize is on for the account, every preview carries one, so content behind
 * a product plays for the operator without a viewer account. `ready` is false while
 * the answer is pending; callers wait before mounting the iframe, so the page never
 * loads twice. With the flag dark, or on an account without Monetize, it is ready at
 * once with no pass. The pass lives ten minutes; the query keeps it five.
 */
export function usePreviewGrant(scope: string | null) {
  const { selectedCId } = useAuth();
  const { status, isLoading: statusLoading } = useMonetizeStatus();
  const enabled = !!status?.enabled && selectedCId != null && scope != null;
  const query = useQuery({
    queryKey: MONETIZE_QUERY_KEYS.previewGrant(selectedCId ?? 0, scope ?? ''),
    queryFn: () => MonetizeApi.previewGrant(selectedCId!, scope!),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });
  const grant = enabled && query.data ? query.data.grant : null;
  const ready = !statusLoading && (!enabled || !query.isPending);
  return { grant, ready };
}
